import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Chip,
  CircularProgress, Alert, IconButton, Tooltip, Button, Paper
} from '@mui/material';
import RefreshIcon        from '@mui/icons-material/Refresh';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import DescriptionIcon    from '@mui/icons-material/Description';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import ErrorIcon          from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ArrowForwardIcon   from '@mui/icons-material/ArrowForward';
import MicIcon            from '@mui/icons-material/Mic';
import { listMeetings }   from '../api';

const STATUS_CONFIG = {
  completed:    { icon: <CheckCircleIcon sx={{ fontSize: 13 }} />,    color: '#10B981', bg: 'rgba(16,185,129,0.12)',  label: 'Completed'  },
  transcribing: { icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} />, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', label: 'Processing' },
  recording:    { icon: <AccessTimeIcon sx={{ fontSize: 13 }} />,     color: '#06B6D4', bg: 'rgba(6,182,212,0.12)',  label: 'Recorded'   },
  error:        { icon: <ErrorIcon sx={{ fontSize: 13 }} />,          color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  label: 'Error'      },
};

export default function MeetingHistory({ onSelectMeeting, showSnackbar }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const fetchMeetings = async () => {
    setLoading(true); setError('');
    try {
      const data = await listMeetings();
      setMeetings(data);
    } catch (err) {
      setError(err.message || 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeetings(); }, []);

  const formatDate = (val) => {
    if (!val) return { date: 'N/A', time: '' };
    try {
      const d = new Date(val);
      return {
        date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
    } catch { return { date: String(val), time: '' }; }
  };

  return (
    <Box className="fade-in-up">

      {/* ══ HEADER ══ */}
      <Card className="glass-card" sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: 16 }}>
                Meeting History
              </Typography>
              <Typography variant="caption" sx={{ color: '#475569', fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>
                {meetings.length} meeting{meetings.length !== 1 ? 's' : ''} stored
              </Typography>
            </Box>
            <Tooltip title="Refresh" arrow>
              <IconButton
                onClick={fetchMeetings}
                disabled={loading}
                sx={{
                  border: '1px solid rgba(139,92,246,0.2)',
                  borderRadius: '12px',
                  color: '#8B5CF6',
                  '&:hover': { background: 'rgba(139,92,246,0.08)', borderColor: '#8B5CF6' },
                  transition: 'all 0.2s',
                }}
              >
                <RefreshIcon sx={{ fontSize: 18, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Box textAlign="center" py={7}>
          <CircularProgress sx={{ color: '#8B5CF6' }} size={36} />
          <Typography variant="body2" sx={{ color: '#475569', mt: 2, fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}>
            Loading from database…
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && <Alert severity="error" sx={{ borderRadius: '14px', mb: 2 }}>{error}</Alert>}

      {/* ══ EMPTY STATE ══ */}
      {!loading && meetings.length === 0 && !error && (
        <Card className="glass-card">
          <CardContent sx={{ p: { xs: 4, md: 7 }, textAlign: 'center' }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'rgba(139,92,246,0.08)',
              border: '1px solid rgba(139,92,246,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 3,
            }}>
              <DescriptionIcon sx={{ fontSize: 38, color: '#374151' }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#475569', mb: 1, fontFamily: '"Space Grotesk", sans-serif' }}>
              No meetings yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#374151', mb: 3 }}>
              Record your first meeting to get started — it will appear here instantly.
            </Typography>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 2.5, py: 1, borderRadius: '12px',
              background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)',
            }}>
              <MicIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
              <Typography variant="caption" sx={{ color: '#A78BFA', fontWeight: 700 }}>
                Switch to Record tab to begin
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ══ TIMELINE MEETING LIST ══ */}
      {!loading && meetings.length > 0 && (
        <Box sx={{ position: 'relative' }}>

          {/* Vertical timeline line */}
          <Box sx={{
            position: 'absolute',
            left: { xs: 14, sm: 19 },
            top: 20, bottom: 20,
            width: 1,
            background: 'linear-gradient(180deg, rgba(139,92,246,0.4), rgba(6,182,212,0.2), transparent)',
            pointerEvents: 'none',
            zIndex: 0,
          }} />

          <Stack spacing={2}>
            {meetings.map((m, index) => {
              const st = STATUS_CONFIG[m.status] || STATUS_CONFIG.recording;
              const { date, time } = formatDate(m.created_at);
              const isEven = index % 2 === 0;

              return (
                <Box key={m.meeting_id} sx={{ position: 'relative', pl: { xs: 4, sm: 5.5 } }}>

                  {/* Timeline dot */}
                  <Box className="timeline-dot" sx={{
                    position: 'absolute',
                    left: { xs: 10, sm: 15 },
                    top: '50%', transform: 'translateY(-50%)',
                    zIndex: 1,
                  }} />

                  {/* Meeting card */}
                  <Card
                    className="glass-card"
                    onClick={() => onSelectMeeting(m)}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 2,
                      // Opaque backgrounds — Lark's webview renders backdrop-filter
                      // glass unreliably, so we don't rely on it for legibility.
                      backdropFilter: 'none !important',
                      WebkitBackdropFilter: 'none !important',
                      background: isEven
                        ? 'rgba(24,22,46,0.98) !important'
                        : 'rgba(13,13,25,0.98) !important',
                      transition: 'all 0.25s cubic-bezier(0.22,1,0.36,1)',
                      '&:hover': {
                        transform: 'translateY(-3px) scale(1.01)',
                        borderColor: 'rgba(139,92,246,0.5) !important',
                        boxShadow: '0 0 32px rgba(139,92,246,0.18), 0 12px 32px rgba(0,0,0,0.5) !important',
                        background: isEven
                          ? 'rgba(34,30,62,0.99) !important'
                          : 'rgba(20,20,38,0.99) !important',
                      },
                      '&:active': {
                        transform: 'translateY(0) scale(0.99)',
                        transition: 'all 0.1s',
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: '16px !important' } }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ sm: 'center' }}>

                        {/* Date/time column */}
                        <Box sx={{ minWidth: { sm: 100 }, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                          <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: '#C4B5FD', fontWeight: 700 }}>
                            {date}
                          </Typography>
                          <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: '#94A3B8' }}>
                            {time}
                          </Typography>
                        </Box>

                        {/* Divider */}
                        <Box sx={{ width: 1, alignSelf: 'stretch', background: 'rgba(139,92,246,0.12)', display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Mobile date */}
                          <Typography sx={{ display: { xs: 'block', sm: 'none' }, fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#94A3B8', mb: 0.5 }}>
                            {date} · {time}
                          </Typography>

                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#F0F4FF', mb: 0.5, fontFamily: '"Space Grotesk", sans-serif', fontSize: 14 }}>
                            {m.title || `Meeting ${(m.meeting_id || '').substring(0, 8)}`}
                          </Typography>

                          {m.full_text && (
                            <Typography variant="caption" sx={{
                              color: '#94A3B8', display: 'block', lineHeight: 1.5,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}>
                              {m.full_text.slice(0, 140)}{m.full_text.length > 140 ? '…' : ''}
                            </Typography>
                          )}
                        </Box>

                        {/* Right: chips + button */}
                        <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-end' }} flexShrink={0}>
                          <Chip
                            icon={<Box sx={{ color: st.color, display: 'flex' }}>{st.icon}</Box>}
                            label={st.label}
                            size="small"
                            sx={{ color: st.color, background: st.bg, border: `1px solid ${st.color}30`, fontWeight: 700, fontSize: 11, height: 24 }}
                          />
                          {m.language && (
                            <Chip
                              label={m.language.toUpperCase()}
                              size="small"
                              sx={{ background: 'rgba(139,92,246,0.1)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.2)', fontWeight: 700, fontSize: 10, height: 22, fontFamily: '"JetBrains Mono", monospace' }}
                            />
                          )}
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 2, py: 0.7, borderRadius: '10px',
                            background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(6,182,212,0.1))',
                            border: '1px solid rgba(139,92,246,0.3)',
                            transition: 'all 0.2s',
                            '&:hover': { background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(6,182,212,0.15))' },
                          }}>
                            <Typography variant="caption" sx={{ color: '#C4B5FD', fontWeight: 800, fontSize: 11 }}>
                              {m.full_text ? 'View Report' : 'Open'}
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: 13, color: '#C4B5FD' }} />
                          </Box>
                        </Stack>

                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ── Spin keyframe for refresh icon ── */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}
